import { Button } from '@nextui-org/react'
import React, { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { Progress } from '@/components/nextui'

interface FormNavigationProps {
    onSubmit: () => void | Promise<void>
    onPrevious: () => void
    submitDisabled?: boolean
    previousDisabled?: boolean
    submitLabel?: string
    previousLabel?: string
    progress: number
    hideSubmit?: boolean
    hidePrevious?: boolean
    showValidationFeedback?: boolean
    triggerValidation?: () => Promise<boolean>
}

export const FormNavigation: React.FC<FormNavigationProps> = ({
    onSubmit,
    onPrevious,
    submitDisabled = false,
    previousDisabled = false,
    submitLabel,
    previousLabel,
    progress,
    hideSubmit = false,
    hidePrevious = false,
    showValidationFeedback = true,
    triggerValidation
}) => {
    const { t } = useTranslation()
    const timeoutRef = useRef<NodeJS.Timeout>()
    const isSubmittingRef = useRef(false)
    const [isLoading, setIsLoading] = React.useState(false)

    // Función para manejar el submit con feedback de validación
    const handleSubmitWithFeedback = useCallback(async () => {
        // Guardas sincrónica para evitar doble clic
        if (isSubmittingRef.current) {
            return
        }

        isSubmittingRef.current = true
        setIsLoading(true)

        try {
            // Si hay feedback habilitado, validar primero
            if (showValidationFeedback && triggerValidation) {
                const isValid = await triggerValidation()

                if (!isValid) {
                    // Buscar el primer campo con error después de la validación
                    setTimeout(() => {
                        const errorElements = document.querySelectorAll(
                            '[data-invalid="true"], .error, [aria-invalid="true"], .text-danger'
                        )

                        if (errorElements.length > 0) {
                            const firstError = errorElements[0] as HTMLElement

                            // Buscar el input asociado
                            const inputElement =
                                firstError.querySelector(
                                    'input, select, textarea'
                                ) ||
                                firstError
                                    .closest('div')
                                    ?.querySelector('input, select, textarea') ||
                                firstError

                            if (inputElement) {
                                inputElement.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                })

                                // Resaltar temporalmente el campo
                                inputElement.classList.add(
                                    'animate-pulse',
                                    'ring-2',
                                    'ring-red-500'
                                )

                                // Quitar el resaltado después de 3 segundos
                                timeoutRef.current = setTimeout(() => {
                                    inputElement.classList.remove(
                                        'animate-pulse',
                                        'ring-2',
                                        'ring-red-500'
                                    )
                                }, 3000)
                            }
                        }
                    }, 100) // Pequeño delay para que React Hook Form actualice el DOM

                    setIsLoading(false)
                    isSubmittingRef.current = false

                    return // No ejecutar onSubmit si hay errores
                }
            }

            // Si es válido o no hay validación, ejecutar el submit
            // Lo esperamos por si es asíncrono
            await onSubmit()
        } catch (error) {
            console.error('Error en proceso de navegación:', error)
        } finally {
            // Importante: No ponemos isSubmitting a false aquí si la navegación fue exitosa
            // y el componente se va a desmontar, pero si falla o hay errores de validación
            // queremos permitir reintentar.
            // Si el componente sigue montado (ej: error en el api), permitimos reintentar.
            setIsLoading(false)
            isSubmittingRef.current = false
        }
    }, [onSubmit, showValidationFeedback, triggerValidation])

    // Cleanup del timeout al desmontar
    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])

    return (
        <div>
            <Progress
                aria-label="progress"
                value={progress}
                className="mt-4"
            />
            <div className="flex items-center justify-between mt-4 space-x-2">
                {!hidePrevious && (
                    <Button
                        color="secondary"
                        onPress={onPrevious}
                        isDisabled={previousDisabled}
                    >
                        {previousLabel || t('forms.ui.previous')}
                    </Button>
                )}

                {!hideSubmit && (
                    <Button
                        color="primary"
                        onPress={handleSubmitWithFeedback}
                        isDisabled={submitDisabled || isLoading}
                        isLoading={isLoading}
                        className=""
                    >
                        {submitLabel || t('forms.ui.next')}
                    </Button>
                )}
            </div>
        </div>
    )
}
